import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronDown, Zap, TrendingDown, TrendingUp, Tag, SlidersHorizontal, 
  Gift, GitCompare, DollarSign, Building, Cpu, Sparkles, BookOpen, 
  FileText, Star, HelpCircle, Wrench, Heart, Bell, AreaChart, ShoppingBag
} from 'lucide-react';
import { PRODUCT_TAGS } from '../../../constants/tags';
import { PRODUCT_CATEGORIES } from './headerConstants';
import { publicFeatures } from '../../../config/publicFeatures';

export function DealsDropdown() {
  return (
    <div className="relative group">
      <button className="flex items-center gap-1 text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors py-2">
        <Gift className="w-4 h-4" />
        Deals
        <ChevronDown className="w-4 h-4" />
      </button>
      <div className="absolute top-full left-0 mt-0 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-[60] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
        <div className="py-2">
          <Link to="/daily-deals" className="flex items-center px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-red-600">
            <Zap className="w-4 h-4 mr-2" /> Daily Deals
          </Link>
          <Link to="/price-drops-today" className="flex items-center px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-green-600">
            <TrendingDown className="w-4 h-4 mr-2" /> Price Drops
          </Link>
        </div>
      </div>
    </div>
  );
}

export function TagsDropdown() {
  return (
    <div className="relative group">
      <button className="flex items-center gap-1 text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors py-2">
        <Tag className="w-4 h-4" />
        Tags
        <ChevronDown className="w-4 h-4" />
      </button>
      
      <div className="absolute top-full left-0 w-[720px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 ease-out z-[60]">
        <div className="grid grid-cols-3 gap-6 p-6">
          <div className="col-span-2 grid grid-cols-2 gap-4">
            {PRODUCT_TAGS.filter(tag => tag.value !== 'all').map(tag => (
              <Link
                key={tag.value}
                to={`/tag/${tag.value}`}
                className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition"
              >
                <div className="text-xl">{tag.icon}</div>
                <div>
                  <div className="font-semibold text-slate-800 dark:text-white">{tag.label}</div>
                  <div className="text-sm text-slate-500">Explore top {tag.label.toLowerCase()}</div>
                </div>
              </Link>
            ))}
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-lg p-5 flex flex-col justify-between">
            <h4 className="text-lg font-semibold mb-2">🔥 Featured Deal</h4>
            
            <div className="w-full aspect-[3/4] rounded-md overflow-hidden shadow-md relative bg-gradient-to-br from-blue-500 to-indigo-600">
              <video
                src="https://cdn.dribbble.com/userupload/17608183/file/original-a9b30b0413131d806620dc5db95c99f1.mp4"
                autoPlay
                muted
                loop
                playsInline
                className="w-[150%] h-full object-cover absolute left-1/2 top-0 transform -translate-x-1/2"
              />
            </div>

            <Link
              to="/daily-deals"
              className="text-sm font-medium underline hover:text-yellow-300"
            >
              View Product →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CategoriesDropdown({ activeCategory, setActiveCategory }) {
  return (
    <div className="group">
      <button className="font-medium text-slate-600 dark:text-slate-300 px-3 py-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-1">
        <SlidersHorizontal className="w-4 h-4" />
        Categories
        <ChevronDown className="w-4 h-4 group-hover:rotate-180 transition-transform"/>
      </button>
      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-full bg-white dark:bg-slate-800 border-y border-slate-200 dark:border-slate-700 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 ease-out transform translate-y-2 group-hover:translate-y-0 z-[60]">
        <div className="container mx-auto px-8 grid grid-cols-12 gap-8">
          <div className="col-span-3 py-6">
              <h3 className="text-sm font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3">Categories</h3>
              <ul>
                {PRODUCT_CATEGORIES.mainCategories.map(cat => (
                  <li key={cat.slug}>
                    <Link 
                        to={cat.slug} 
                        onMouseEnter={() => setActiveCategory(cat)}
                      className={`relative w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${activeCategory.slug === cat.slug ? 'bg-blue-50 text-blue-600 dark:bg-slate-700' : 'hover:bg-slate-100 dark:hover:bg-slate-700/50 text-slate-700 dark:text-slate-300'}`}
                      >
                      <cat.icon className="w-5 h-5" />
                      <span className="font-semibold">{cat.name}</span>
                    </Link>
                  </li>
                ))}
            </ul>
          </div>

          <div className="col-span-5 py-6 border-l border-slate-200 dark:border-slate-700">
            {activeCategory && (
              <div className="pl-8 animate-fade-in">
                <Link to={activeCategory.slug} className="text-lg font-bold text-slate-800 dark:text-white hover:text-blue-600 dark:hover:text-blue-400">Shop all {activeCategory.name}</Link>
                <div className="grid grid-cols-2 gap-x-8 mt-4">
                  {activeCategory.tags.length > 0 && (
                    <div>
                        <h4 className="text-sm font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3">Popular Tags</h4>
                        <ul className="space-y-2">
                            {activeCategory.tags.map(tag => (
                                <li key={tag.slug}><Link to={tag.slug} className="text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400">{tag.name}</Link></li>
                            ))}
                        </ul>
                    </div>
                  )}
                  {activeCategory.brands.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3">Top Brands</h4>
                      <ul className="space-y-2">
                          {activeCategory.brands.map(brand => (
                            <li key={brand.slug}><Link to={brand.slug} className="text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400">{brand.name}</Link></li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className="col-span-4 p-6">
            {publicFeatures.dailyDeals && activeCategory && activeCategory.promo && (
              <Link to={activeCategory.promo.link} className="block rounded-lg overflow-hidden group/promo h-full relative animate-fade-in">
                  <img src={activeCategory.promo.imageUrl} alt={activeCategory.promo.title} className="w-full h-full object-cover transition-transform duration-300 group-hover/promo:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 p-6 text-white">
                      <h4 className="font-bold text-xl">{activeCategory.promo.title}</h4>
                      <p className="text-sm opacity-90">{activeCategory.promo.description}</p>
                  </div>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function BrowseDropdown() {
  return (
    <div className="relative group">
      <button className="flex items-center gap-1 text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors py-2">
        <Link to="/pages" className="flex items-center gap-1 px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600">
          <ShoppingBag className="w-4 h-4" />
          Pages
          <ChevronDown className="w-4 h-4" />
        </Link>
      </button>
      <div className="absolute top-full left-0 mt-0 w-52 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-[60] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
        <div className="py-2">
          <Link to="/deal" className="flex items-center px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600">
            <GitCompare className="w-4 h-4 mr-2" />Tech Deals Today
          </Link>
          <Link to="/trending" className="flex items-center px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600">
            <TrendingUp className="w-4 h-4 mr-2" />Trending Tech Products
          </Link>
          <Link to="/new-releases" className="flex items-center px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600">
            <Gift className="w-4 h-4 mr-2" />New Tech Releases
          </Link>
          <Link to="/use-case" className="flex items-center px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600">
            <Cpu className="w-4 h-4 mr-2" />Best Tech for Your Needs
          </Link>
          <Link to="/feature" className="flex items-center px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600">
            <Sparkles className="w-4 h-4 mr-2" />Best Tech by Features
          </Link>
          <Link to="/budget" className="flex items-center px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600">
            <DollarSign className="w-4 h-4 mr-2" />Best Tech by Budget
          </Link>
          <Link to="/brand" className="flex items-center px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600">
            <Building className="w-4 h-4 mr-2" />Best Tech Brands
          </Link>
        </div>
      </div>
    </div>
  );
}

export function ResourcesDropdown() {
  return (
    <div className="relative group">
      <button className="flex items-center gap-1 text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors py-2">
        <BookOpen className="w-4 h-4" />
        Resources
        <ChevronDown className="w-4 h-4" />
      </button>
      <div className="absolute top-full left-0 mt-0 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-[60] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
        <div className="py-2">
          <Link to="/buying-guides" className="flex items-center px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600">
            <FileText className="w-4 h-4 mr-2" />Buying Guides
          </Link>
          <Link to="/reviews" className="flex items-center px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600">
            <Star className="w-4 h-4 mr-2" />Product Reviews
          </Link>
          <Link to="/faqs" className="flex items-center px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600">
            <HelpCircle className="w-4 h-4 mr-2" />FAQs
          </Link>
        </div>
      </div>
    </div>
  );
}

export function AccountDropdown() {
  return (
    <div className="relative group">
      <button className="flex items-center gap-1 text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors py-2">
        <Wrench className="w-4 h-4" />
        Account
        <ChevronDown className="w-4 h-4" />
      </button>
      <div className="absolute top-full right-0 mt-0 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-[60] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
        <div className="py-2">
          <Link to="/wishlist" className="flex items-center px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600">
            <Heart className="w-4 h-4 mr-2" />Wishlist
          </Link>
          <Link to="/tools/price-tracker" className="flex items-center px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600">
            <Bell className="w-4 h-4 mr-2" />Price Tracker & Alerts
          </Link>
          <Link to="/tools/price-history" className="flex items-center px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600">
            <AreaChart className="w-4 h-4 mr-2" />Price History Charts
          </Link>
        </div>
      </div>
    </div>
  );
}
